import { motion } from "framer-motion";
import { ArrowDown, Instagram } from "lucide-react";

export default function Hero() {
    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <section
            id="accueil"
            data-testid="hero-section"
            className="relative min-h-screen flex items-center pt-24 overflow-hidden bg-cream"
        >
            <div className="max-w-6xl mx-auto px-6 md:px-12 w-full grid md:grid-cols-12 gap-12 items-center">
                {/* Left – text */}
                <div className="md:col-span-7 relative z-10">
                    <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-rose-700 mb-8"
                        data-testid="hero-eyebrow"
                    >
                        <span className="w-8 h-px bg-rose-700" />
                        Accompagnement périnatal · Montpellier & alentours
                    </motion.span>

                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.1 }}
                        className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-ink text-balance"
                        data-testid="hero-title"
                    >
                        Une présence douce
                        <br />
                        sur le chemin de la{" "}
                        <span className="italic text-rose-700">naissance</span>.
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.25 }}
                        className="mt-8 max-w-xl text-base md:text-lg text-ink-soft leading-relaxed"
                        data-testid="hero-subtitle"
                    >
                        Accompagnement à la naissance et préparation à la périnatalité —
                        un soutien bienveillant, respectueux et personnalisé pour les
                        futurs et jeunes parents.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.4 }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <button
                            data-testid="hero-cta-contact"
                            onClick={() => scrollTo("contact")}
                            className="rounded-full bg-rose-700 text-cream px-7 py-3.5 text-sm tracking-wide hover:bg-rose-800 transition-colors duration-300"
                        >
                            Me contacter
                        </button>
                        <button
                            data-testid="hero-cta-services"
                            onClick={() => scrollTo("services")}
                            className="rounded-full border border-line text-ink px-7 py-3.5 text-sm tracking-wide hover:border-rose-700 hover:text-rose-700 transition-all duration-300"
                        >
                            Découvrir les services
                        </button>
                        <a
                            data-testid="hero-instagram"
                            href="https://www.instagram.com/cindy_bien_naitre/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-2 inline-flex items-center gap-2 text-sm text-ink-soft hover:text-rose-700 transition-colors"
                        >
                            <Instagram size={18} />
                            @cindy_bien_naitre
                        </a>
                    </motion.div>
                </div>

                {/* Right – image */}
                <motion.div
                    initial={{ opacity: 0, scale: 1.04 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1.2, ease: "easeOut" }}
                    className="md:col-span-5 relative"
                >
                    <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden bg-sand">
                        <img
                            src="https://images.pexels.com/photos/33665727/pexels-photo-33665727.jpeg"
                            alt="Femme enceinte au coucher du soleil"
                            className="w-full h-full object-cover"
                            data-testid="hero-image"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-cream/30 via-transparent to-transparent" />
                    </div>
                    <div className="hidden md:block absolute -bottom-8 -left-10 bg-cream border border-line rounded-2xl px-6 py-5 max-w-[240px]">
                        <p className="font-serif text-2xl text-ink leading-snug italic">
                            « Naître bien, accompagner avec cœur. »
                        </p>
                    </div>
                </motion.div>
            </div>

            <button
                onClick={() => scrollTo("a-propos")}
                className="absolute bottom-10 left-1/2 -translate-x-1/2 text-ink-soft hover:text-rose-700 transition-colors animate-bounce"
                aria-label="Faire défiler"
                data-testid="hero-scroll-down"
            >
                <ArrowDown size={22} />
            </button>
        </section>
    );
}
